/**
 * Property strip (design §3 / fullscreen-spec §2): light-gray band under the
 * top bar with the unit facts left; the right cluster is kind-conditional —
 * pre-screen keeps the dark screening-kind pill (its design authority is
 * unchanged), full screening replaces it with the Combined|Individual toggle
 * group. Facts without data drop out of the row. The actions kebab rides in
 * the `actions` slot at the end of that cluster (client 2026-08-27 — it used
 * to sit in the sticky top bar, beside Accept/Decline).
 */
import { Bath, BedDouble, House, Receipt } from 'lucide-react'
import type { ReactNode } from 'react'
import { StudioIcon } from '#app/components/application-detail-v2/vendor/studio-icon'
import {
	SCREENING_KIND_LABELS,
	countLabel,
} from '#app/utils/application-detail-v2/display'
import type {
	GroupMember,
	PendingInvitationSummary,
	PropertySummary,
	ScreeningKind,
} from '#app/utils/application-detail-v2/types'
import { formatBedroomsShortSingular } from '#app/utils/application-detail-v2/vendor/format-bedrooms'
import { cn } from '#app/utils/misc'
import { ApplicantSwitcher } from './ApplicantSwitcher'
import { DETAIL_CONTAINER } from './primitives'

/** One option inside a white fully-rounded toggle pill (fullscreen-spec §2):
 * active = dark charcoal pill with white text; inactive = plain dark text. */
function ToggleOption({
	label,
	active,
	disabled,
	title,
	onSelect,
}: {
	label: string
	active: boolean
	disabled?: boolean
	title?: string
	onSelect?: () => void
}) {
	return (
		<button
			type="button"
			disabled={disabled}
			title={title}
			aria-pressed={active}
			onClick={onSelect}
			className={cn(
				'rounded-full px-3 py-1.5 text-sm transition-colors sm:px-4 sm:text-[15px]',
				active ? 'bg-[#32302F] font-medium text-white' : 'text-[#32302F]',
				disabled ? 'cursor-not-allowed' : 'cursor-pointer',
			)}
		>
			{label}
		</button>
	)
}

const TOGGLE_GROUP =
	'flex items-center gap-1 rounded-full border border-[#CCCCCC] bg-[#f4f6f6] py-1 p-1.5'

interface StripFact {
	key: string
	icon: React.ReactNode
	label: string
	emphasis?: boolean
}

/** Per-fact icon tints; facts without an entry keep the default gray. */
const FACT_ICON_TINT: Record<string, string> = {
	identity: '[&>svg]:text-[#599BF8]',
	beds: '[&>svg]:text-[#A5BB83]',
	baths: '[&>svg]:text-[#BE7B97]',
	rent: '[&>svg]:text-[#16A34A]',
}

/** Strip items in design order (§3). */
function stripFacts(property: PropertySummary): StripFact[] {
	const number = (value: number) => value.toLocaleString('en-US')
	// The property identity (Phase 2): a real name if named, else the full
	// address — the same string the PDFs and every other surface now show.
	const facts: StripFact[] = [
		{ key: 'identity', icon: <House />, label: property.title },
	]
	if (property.bedrooms != null) {
		facts.push({
			key: 'beds',
			// Studio (0 bedrooms) flips to the warehouse glyph, matching the
			// property detail page and the PDF reports; other counts keep the bed.
			icon: property.bedrooms === 0 ? <StudioIcon /> : <BedDouble />,
			// Studio (0 bedrooms) must read "Studio", not "0 Beds" (TT2-494); the
			// owner wants the singular "3 Bed" (not "3 Beds"), matching the
			// property detail page and the reports/PDFs. Fall back to the plain
			// count for any value formatBedroomsShortSingular can't parse.
			label:
				formatBedroomsShortSingular(property.bedrooms) ??
				countLabel(property.bedrooms, 'Bed', 'Bed'),
		})
	}
	// A studio (0 bedrooms) has no bathroom fact — the item is dropped even if
	// a legacy unit row still carries a bathroom count. The owner wants the
	// singular "2 Bath" (not "2 Baths").
	if (property.bathrooms != null && property.bedrooms !== 0) {
		facts.push({
			key: 'baths',
			icon: <Bath />,
			label: countLabel(property.bathrooms, 'Bath', 'Bath'),
		})
	}
	if (property.unitNumber) {
		facts.push({
			key: 'unit',
			icon: <img src="/img/unit-icon.svg" alt="" className="size-4 shrink-0" />,
			label: `Unit ${property.unitNumber}`,
		})
	}
	if (property.squareFeet != null) {
		facts.push({
			key: 'sqft',
			// No thousands separator — the PNG shows '1000 Sqft.' (rent keeps its
			// separator).
			icon: <img src="/img/sqft-icon.svg" alt="" className="size-4 shrink-0" />,
			label: `${property.squareFeet} Sqft.`,
		})
	}
	if (property.monthlyRent != null) {
		facts.push({
			key: 'rent',
			icon: <Receipt />,
			label: `$ ${number(property.monthlyRent)}`,
			emphasis: true,
		})
	}
	return facts
}

export function PropertyStrip({
	property,
	groupMembers,
	propertyId,
	currentName,
	currentApplicationId,
	pendingInvitations,
	screeningKind,
	view = 'individual',
	onViewChange,
	actions,
}: {
	property: PropertySummary
	groupMembers: GroupMember[]
	propertyId: string
	currentName: string
	/** Highlights the member whose report is on screen (switcher). */
	currentApplicationId?: string
	/** Invited-but-not-joined members — muted switcher rows (2026-07-13). */
	pendingInvitations?: PendingInvitationSummary[]
	screeningKind: ScreeningKind
	/** Which report view this strip sits on; the other option navigates
	 * (COMBINED-V2-DATA-MAP §1 — combined is a route, not a tab). */
	view?: 'individual' | 'combined'
	onViewChange?: (view: 'individual' | 'combined') => void
	/** Trailing slot in the right cluster — the actions kebab lives here
	 * (client 2026-08-27, moved out of the sticky top bar). */
	actions?: ReactNode
}) {
	// A guarantor's own application has no screening-kind variant, so the
	// pre-screen kind pill is suppressed for it (user 2026-06-24) — only the
	// default strip (the applicant switcher) shows. The current member is the
	// one highlighted in `groupMembers`.
	const isGuarantorApplication =
		groupMembers.find((member) => member.applicationId === currentApplicationId)
			?.applicationType === 'guarantor'
	// A lone applicant has nothing to combine, so the toggle waits until a
	// household actually exists — a co-applicant or guarantor row, or an invite
	// already sent (client 2026-08-27, narrowing the 2026-06-13 "solo full
	// households too" directive). Pending invites count so the control does not
	// vanish between sending one and its acceptance.
	// Always available while ON the combined view: that is the only way back to
	// Individual, and hiding it would strand a direct link (the same stranding
	// the 2026-07-14 fix removed for pre-screen guarantors).
	const hasHousehold =
		view === 'combined' ||
		groupMembers.length > 1 ||
		(pendingInvitations?.length ?? 0) > 0
	// The toggle GROUP belongs to full screening, reachable from ANY member of a
	// household that holds at least one (2026-07-14 — previously a pre-screen
	// guarantor entry lost it entirely and could never navigate back to
	// Combined). A fully pre-screen household has no combined surface and shows
	// its kind pill instead.
	const toggleAvailable =
		screeningKind === 'full' ||
		groupMembers.some((member) => member.screeningKind === 'full')
	// Only the COMBINED option waits for a household; Individual still shows,
	// so a solo applicant keeps the view indicator.
	const combinedAvailable = toggleAvailable && hasHousehold
	return (
		<div className="border-b border-[#EDEDEB] bg-[#f4f6f6]">
			<div
				className={cn(
					DETAIL_CONTAINER,
					'flex min-h-16 flex-wrap items-center justify-between gap-x-4 gap-y-2 py-2 min-[1025px]:flex-nowrap sm:gap-x-7',
				)}
			>
				<div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1.5 sm:gap-x-7 sm:gap-y-2">
					{stripFacts(property).map((fact) => (
						<span
							key={fact.key}
							className={cn(
								'flex items-center gap-1.5 sm:gap-2 [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-[#747474]',
								FACT_ICON_TINT[fact.key],
								// Only the address gives way under pressure; the short facts
								// keep their full width so they never truncate mid-line.
								fact.key === 'identity'
									? 'min-w-0'
									: 'shrink-0 whitespace-nowrap',
							)}
						>
							{fact.icon}
							<span
								className={cn(
									'truncate text-sm text-[#32302F]',
									fact.emphasis && 'text-base font-bold sm:text-lg',
								)}
							>
								{fact.label}
							</span>
						</span>
					))}
				</div>
				<div className="flex w-full flex-wrap items-center gap-3 min-[1025px]:shrink-0 min-[1025px]:flex-nowrap sm:w-auto sm:gap-5">
					<ApplicantSwitcher
						groupMembers={groupMembers}
						propertyId={propertyId}
						currentName={currentName}
						currentApplicationId={currentApplicationId}
						pendingInvitations={pendingInvitations}
					/>
					{/* Combined is reachable from ANY member of a household holding
					    at least one full screening, even solo full households
					    (user directives 2026-06-13 + 2026-07-14) — the inactive
					    option NAVIGATES (COMBINED-V2-DATA-MAP §1). A pre-screen
					    entry keeps its kind pill NEXT TO the toggle; a fully
					    pre-screen household has no combined surface → no toggle. */}
					{toggleAvailable ? (
						<div className={TOGGLE_GROUP}>
							{combinedAvailable ? (
								<ToggleOption
									label="Combined"
									active={view === 'combined'}
									onSelect={() => onViewChange?.('combined')}
								/>
							) : null}
							<ToggleOption
								label="Individual"
								active={view === 'individual'}
								onSelect={() => onViewChange?.('individual')}
							/>
						</div>
					) : null}
					{/* The Simple|Enterprise depth toggle was REMOVED (client 2026-08-27):
						every report renders at enterprise depth, so the control had only
						one reachable state. Full screening shows nothing here now;
						pre-screen keeps its kind pill. */}
					{screeningKind === 'full' || isGuarantorApplication ? null : (
						// Pre-screen keeps its dark kind pill (spec §2: the pill is
						// ABSENT in the full-screening design). Suppressed for a
						// guarantor's own application (user 2026-06-24) — a guarantor
						// report has no screening-kind variant, so its strip just shows
						// the default (the applicant switcher).
						<span className="rounded-full bg-[#32302F] px-4 py-1.5 text-sm font-medium text-white sm:px-6 sm:py-2 sm:text-[15px]">
							{SCREENING_KIND_LABELS[screeningKind]}
						</span>
					)}
					{actions}
				</div>
			</div>
		</div>
	)
}
